import * as React from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Popover from "@mui/material/Popover";
import List from "@mui/material/List";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {
  createStoreInfoLookup,
  groupStoreOptionsByCompany,
} from "../../../../helpers/storeOptions";

export default function StoreName(props) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const anchorRef = React.useRef(null);
  const textRef = React.useRef(null);

  const storeInfoLookup = React.useMemo(
    () => createStoreInfoLookup(props.storeNames),
    [props.storeNames]
  );

  const get_store_name = React.useCallback(
    (storeId) => storeInfoLookup.get(storeId)?.store_name ?? null,
    [storeInfoLookup]
  );

  const filteredStoreOptions = React.useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return (props.storeOptions || []).filter((option) => {
      const storeName = get_store_name(option.store);

      if (!storeName) {
        return false;
      }

      if (!search) {
        return true;
      }

      return storeName.toLowerCase().includes(search);
    });
  }, [props.storeOptions, searchTerm, get_store_name]);

  const groupedStoreOptions = React.useMemo(
    () => groupStoreOptionsByCompany(filteredStoreOptions, storeInfoLookup),
    [filteredStoreOptions, storeInfoLookup]
  );

  const showCompanyLabels = groupedStoreOptions.length > 1;

  const handleChange = (event) => {
    props.change_home_store(event.target.value);
    setOpen(false);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
  };

  React.useEffect(() => {
    setSearchTerm("")
  }, [open])

  React.useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [props.homeStore])

  return (
    <Box>
      <FormControl fullWidth size="small">
        {props.storeOptions.length > 1 ?
          <Tooltip
            title={isTruncated ? get_store_name(props.homeStore) : ""}
            placement="bottom"
          >
            <div
              ref={anchorRef}
              className="flex items-center gap-x-0.5 cursor-pointer"
              onClick={handleOpen}
            >
              <p ref={textRef} className="text-slate-900 truncate max-w-[220px]">{get_store_name(props.homeStore)}</p>
              <div className="text-slate-500 flex-shrink-0">
                {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </div>
            </div>
          </Tooltip> :
          <Tooltip
            title={isTruncated ? get_store_name(props.homeStore) : ""}
            placement="bottom"
          >
            <div>
              <p ref={textRef} className="text-slate-900 truncate max-w-[220px]">{get_store_name(props.homeStore)}</p>
            </div>
          </Tooltip>
        }
        <Popover
          open={open}
          anchorEl={anchorRef.current}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
        >
          <Box p={2} width="300px">
            <TextField
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              fullWidth
              size="small"
              autoFocus
              InputProps={{
                sx: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  padding: "5px",
                  height: "42px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
            <List>
              {groupedStoreOptions.map((group) => (
                <React.Fragment
                  key={group.companyId || `company-${group.companyName}`}
                >
                  {showCompanyLabels && (
                    <MenuItem
                      disabled
                      sx={{
                        fontWeight: "bold",
                        opacity: 1,
                        "&.Mui-disabled": {
                          opacity: 1,
                        },
                      }}
                    >
                      {group.companyName}
                    </MenuItem>
                  )}
                  {group.options.map((option) => (
                    <MenuItem
                      key={option.store}
                      value={option.store}
                      onClick={() =>
                        handleChange({ target: { value: option.store } })
                      }
                      selected={props.homeStore === option.store}
                    >
                      {get_store_name(option.store)}
                    </MenuItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Popover>
      </FormControl>
    </Box>
  );
}
